import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/admin/Dashboard';
import Pedidos from './pages/admin/Pedidos';
import Products from './pages/admin/Products';
import Categories from './pages/admin/Categories';
import Settings from './pages/admin/Settings';
import Reports from './pages/admin/Reports';
import OrderHistory from './pages/admin/OrderHistory';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import Promocoes from './pages/admin/Promocoes';
import StorePage from './pages/store/StorePage';
import OrderTracking from './pages/store/OrderTracking';

// Loading Component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
    </div>
  );
}

// Protected Route for Establishment Admins
function EstablishmentRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  
  return children;
}

// Protected Route for Super Admin
function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/admin/dashboard" replace />;
  
  return children;
}

// Public Route - Redirect based on role
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  
  if (user) {
    if (user.role === 'super_admin') {
      return <Navigate to="/super-admin" replace />;
    }
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Auth Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/recuperar-senha" element={<ResetPassword />} />
      
      {/* Super Admin Route */}
      <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
      
      {/* Establishment Admin Routes */}
      <Route path="/admin/dashboard" element={<EstablishmentRoute><Dashboard /></EstablishmentRoute>} />
      <Route path="/admin/pedidos" element={<EstablishmentRoute><Pedidos /></EstablishmentRoute>} />
      <Route path="/admin/products" element={<EstablishmentRoute><Products /></EstablishmentRoute>} />
      <Route path="/admin/categories" element={<EstablishmentRoute><Categories /></EstablishmentRoute>} />
      <Route path="/admin/reports" element={<EstablishmentRoute><Reports /></EstablishmentRoute>} />
      <Route path="/admin/settings" element={<EstablishmentRoute><Settings /></EstablishmentRoute>} />
      <Route path="/admin/history" element={<EstablishmentRoute><OrderHistory /></EstablishmentRoute>} />
      <Route path="/admin/promocoes" element={<EstablishmentRoute><Promocoes /></EstablishmentRoute>} />
      
      {/* Public Store Routes */}
      <Route path="/loja/:slug" element={<StorePage />} />
      <Route path="/acompanhar/:orderId" element={<OrderTracking />} />
      
      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
